import React, { useState } from 'react';
import type { Issue } from '../../types/issue';
import { ROOT_CAUSE_CATEGORY_LABELS } from '../../types/issue';
import {
  ACTION_PRIORITY_LABELS,
  ACTION_STATUS_LABELS,
  type EnterpriseAction,
} from '../../services/actionService';
import type { ActionCountermeasureRecord } from '../../services/actionCountermeasureService';
import type { CauseCheckView } from '../../utils/causeWork';
import { consistencySummary } from '../../utils/causeWork';
import { isCauseConfirmed } from '../../utils/problemCause';
import {
  confirmedHypothesisContext,
  countermeasureSummary,
  fiveW2HCompleteness,
} from '../../utils/countermeasurePlan';

interface ActionPlanWorkspaceProps {
  issue: Issue;
  actions: EnterpriseAction[];
  assigneeNames: Record<string, string>;
  extensions: ActionCountermeasureRecord[];
  causeCheck: CauseCheckView | null;
  onCreate: () => void;
  onEdit: (action: EnterpriseAction) => void;
  onCancel: (action: EnterpriseAction) => void;
  onOpenAnalysis: () => void;
  actionsState?: 'loading' | 'error';
}

export const ActionPlanWorkspace: React.FC<ActionPlanWorkspaceProps> = ({
  issue,
  actions,
  assigneeNames,
  extensions,
  causeCheck,
  onCreate,
  onEdit,
  onCancel,
  onOpenAnalysis,
  actionsState,
}) => {
  const confirmed = isCauseConfirmed(issue);
  const analysis = issue.root_cause_analysis;
  const hypotheses = confirmedHypothesisContext(analysis);
  const extensionOf = (actionId: string) => extensions.find((item) => item.actionId === actionId) ?? null;

  if (actionsState && actions.length === 0) {
    return (
      <p className="text-[13px] text-slate-600">
        {actionsState === 'loading' ? 'Carregando contramedidas.' : 'Não foi possível carregar as contramedidas.'}
      </p>
    );
  }

  if (!confirmed && actions.length === 0) {
    return (
      <div className="max-w-xl">
        <p className="text-[13px] font-medium text-slate-900">Plano de ação ainda não iniciado</p>
        <p className="mt-1 text-[13px] text-slate-600">A causa precisa ser confirmada antes da definição das contramedidas.</p>
        <button type="button" onClick={onOpenAnalysis} className="mt-2 text-[12px] font-medium text-slate-700 hover:underline">
          Voltar para Análise
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-[960px] space-y-4">
      <section className="max-w-[720px]">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
          {confirmed ? 'Causa confirmada' : 'Causa a tratar'}
        </p>
        <p className="mt-1 text-[14px] font-medium text-slate-950">
          {analysis?.root_cause_description?.trim() || issue.root_cause?.trim() || 'A causa ainda não está consolidada.'}
        </p>
        {analysis?.root_cause_category ? (
          <p className="text-[12px] text-slate-500">{ROOT_CAUSE_CATEGORY_LABELS[analysis.root_cause_category]}</p>
        ) : null}
        {analysis?.cause_evidence?.trim() ? (
          <p className="mt-1 text-[13px] text-slate-700">Evidência: {analysis.cause_evidence.trim()}</p>
        ) : null}
        {hypotheses.length > 0 ? (
          <p className="mt-1 text-[12px] text-slate-500">Hipóteses confirmadas: {hypotheses.join('; ')}</p>
        ) : null}
        {causeCheck ? (
          <p className="mt-1 text-[12px] text-slate-500">Consistência: {consistencySummary(causeCheck)}</p>
        ) : null}
        <button type="button" onClick={onOpenAnalysis} className="mt-1 text-[12px] font-medium text-slate-700 hover:underline">
          Ver análise
        </button>
      </section>

      <p className="text-[12px] text-slate-400">tratar com</p>

      <section>
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-[13px] font-semibold text-slate-900">Contramedidas</h3>
          {confirmed && issue.status !== 'closed' ? (
            <button type="button" onClick={onCreate} className="text-[12px] font-medium text-slate-700 hover:underline">
              + Nova contramedida
            </button>
          ) : null}
        </div>
        {actions.length > 0 ? (
          <p className="mt-1 text-[12px] text-slate-500">{countermeasureSummary(actions)}</p>
        ) : (
          <p className="mt-1 text-[13px] text-slate-600">
            {actionsState === 'loading' ? 'Carregando contramedidas.' : actionsState === 'error' ? 'Não foi possível carregar as contramedidas.' : 'Nenhuma contramedida definida.'}
          </p>
        )}
        {actions.length > 0 ? (
          <div className="mt-2 border-t border-slate-200">
            <div className="grid grid-cols-[minmax(0,1.7fr)_minmax(0,0.9fr)_5.5rem_4.75rem_6.5rem_5.75rem_auto] gap-x-3 border-b border-slate-200 py-1 text-[11px] font-medium text-slate-500">
              <span>Contramedida</span>
              <span>Responsável</span>
              <span>Prazo</span>
              <span>Prioridade</span>
              <span>5W2H</span>
              <span>Status</span>
              <span className="sr-only">Ações</span>
            </div>
            <ul>
              {actions.map((action) => (
                <CountermeasureRow
                  key={action.id}
                  action={action}
                  assignee={action.assigned_to ? (assigneeNames[action.assigned_to] ?? 'Responsável') : '—'}
                  extension={extensionOf(action.id)}
                  onEdit={() => onEdit(action)}
                  onCancel={() => onCancel(action)}
                />
              ))}
            </ul>
          </div>
        ) : null}
      </section>
    </div>
  );
};

function formatDue(value: string | null): string {
  if (!value) return '—';
  const [year, month, day] = value.slice(0, 10).split('-');
  if (!year || !month || !day) return '—';
  return `${day}/${month}/${year}`;
}

function CountermeasureRow({
  action,
  assignee,
  extension,
  onEdit,
  onCancel,
}: {
  action: EnterpriseAction;
  assignee: string;
  extension: ActionCountermeasureRecord | null;
  onEdit: () => void;
  onCancel: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const cancelled = action.status === 'cancelled';
  const completeness = fiveW2HCompleteness({
    title: action.title,
    description: action.description,
    assignedTo: action.assigned_to,
    dueDate: action.due_date,
    whereText: extension?.whereText ?? null,
    howText: extension?.howText ?? null,
    costAmount: extension?.costAmount ?? null,
  });
  const place = [extension?.whereText, extension?.howText].filter(Boolean).join(' · ');

  return (
    <li className={`grid grid-cols-[minmax(0,1.7fr)_minmax(0,0.9fr)_5.5rem_4.75rem_6.5rem_5.75rem_auto] items-center gap-x-3 border-b border-slate-100 py-1.5 text-[12px] ${cancelled ? 'text-slate-400' : 'text-slate-800'}`}>
      <div className="min-w-0">
        <p className={`truncate ${cancelled ? 'line-through' : ''}`}>{action.title}</p>
        {place ? <p className="truncate text-[11px] text-slate-500">{place}</p> : null}
      </div>
      <span className="truncate">{assignee}</span>
      <span>{formatDue(action.due_date)}</span>
      <span>{ACTION_PRIORITY_LABELS[action.priority]}</span>
      <span>{completeness.label}</span>
      <span>{ACTION_STATUS_LABELS[action.status]}</span>
      <span className="flex justify-end gap-2 whitespace-nowrap">
        {cancelled ? null : (
          <button type="button" onClick={onEdit} className="font-medium text-slate-600 hover:underline">Editar</button>
        )}
        {cancelled ? null : confirming ? (
          <button type="button" onClick={onCancel} className="font-medium text-slate-600 hover:underline">Confirmar cancelamento</button>
        ) : (
          <button type="button" onClick={() => setConfirming(true)} className="font-medium text-slate-500 hover:underline">Cancelar</button>
        )}
      </span>
    </li>
  );
}
